// src/components/resources/tools/generators/SocialMediaMockup/VerticalVideoPreview.tsx

'use client'

import type { FC } from 'react'
import { Bookmark, Camera, Heart, MessageCircle, Music, Play, Plus, Share2 } from 'lucide-react'
import { clsx } from 'clsx'
import Image from 'next/image'
import { InstagramVerifiedBadge } from './VerifiedBadges'
import { formatMetric, formatVerticalHandle } from './socialMediaFormatting'
import type { VerticalVideoPreviewProps } from './types'

/**
 * Full-bleed 9:16 vertical preview shared by TikTok and Instagram Reels.
 *
 * Ports the structure of the portfolio's TikTok/Reels layouts: media fills
 * the frame, a bottom gradient keeps the caption legible, the author +
 * caption + sound row sit bottom-left, and a right-hand action rail carries
 * the like / comment / save / share affordances. The two variants share
 * everything but a few brand accents (top label, follow-button color, the
 * rail's primary tint).
 */
export const VerticalVideoPreview: FC<VerticalVideoPreviewProps> = ({ config, onInteraction, variant }): JSX.Element => {
  const isTikTok = variant === 'tiktok'
  const currentLikes = config.interactions.liked ? config.metrics.likes + 1 : config.metrics.likes
  const handle = formatVerticalHandle(config)
  const followColor = isTikTok ? '#FE2C55' : '#FF3040'
  const frameBackground =
    config.background.trim().length > 0 ? config.background : 'linear-gradient(160deg,#1b2440,#101522)'
  const soundLabel = config.soundName && config.soundName.trim().length > 0 ? config.soundName : 'original sound'

  return (
    <div className="relative mx-auto aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-[28px] bg-black shadow-2xl">
      {/* Media fill */}
      {config.mediaUrl ? (
        <Image
          src={config.mediaUrl}
          alt="Reel media"
          fill
          className="object-cover"
          sizes="320px"
          unoptimized
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: frameBackground }}
        >
          <div className="flex h-full w-full items-center justify-center">
            <Play className="h-14 w-14 text-white/40" />
          </div>
        </div>
      )}

      {/* Top label row */}
      <div className="absolute inset-x-0 top-0 flex items-center justify-center gap-5 pt-3 text-[13px] font-semibold text-white drop-shadow">
        {isTikTok ? (
          <>
            <span className="text-white/60">Following</span>
            <span className="relative">
              For You
              <span className="absolute -bottom-1 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-white" />
            </span>
          </>
        ) : (
          <span>Reels</span>
        )}
      </div>

      {/* Legibility gradient */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 to-transparent" />

      {/* Bottom-left author / caption / sound */}
      <div className="absolute bottom-3 left-3 right-16 text-white drop-shadow">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-bold">@{handle}</span>
          {config.verified &&
            (isTikTok ? (
              <span role="img" aria-label="Verified" className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#20D5EC] text-[9px] font-bold text-black">
                ✓
              </span>
            ) : (
              <InstagramVerifiedBadge />
            ))}
        </div>
        <p className="mt-1 line-clamp-3 text-[13px] leading-snug">{config.content}</p>
        {config.hashtags && <p className="mt-0.5 text-[13px] font-medium text-white/90">{config.hashtags}</p>}
        <div className="mt-2 flex items-center gap-1.5 text-[12px]">
          <Music className="h-3.5 w-3.5" />
          <span className="truncate">{soundLabel}</span>
        </div>
      </div>

      {/* Right-hand action rail. On the vertical formats this rail IS platform
          chrome (avatar/follow, action icons, sound disc), so it always renders;
          only the engagement counts are suppressed when metrics are hidden. */}
      <div className="absolute bottom-3 right-2 flex flex-col items-center gap-4 text-white">
        {/* Avatar + follow */}
        <div className="relative mb-1">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gradient-to-br from-purple-500 to-pink-500">
            {config.avatarUrl ? (
              <Image src={config.avatarUrl} alt="" width={44} height={44} className="h-full w-full object-cover" unoptimized />
            ) : (
              <Camera className="h-5 w-5 text-white" />
            )}
          </div>
          <span
            className="absolute -bottom-2 left-1/2 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full"
            style={{ backgroundColor: followColor }}
          >
            <Plus className="h-3 w-3 text-white" />
          </span>
        </div>

        <button type="button" onClick={() => onInteraction('liked')} className="flex flex-col items-center gap-1" aria-label="Like">
          <Heart className={clsx('h-8 w-8 drop-shadow', config.interactions.liked ? 'fill-[#FE2C55] text-[#FE2C55]' : 'fill-white text-white')} />
          {config.showMetrics && <span className="text-[11px] font-semibold">{formatMetric(currentLikes)}</span>}
        </button>

        <div className="flex flex-col items-center gap-1">
          <MessageCircle className="h-8 w-8 fill-white text-white drop-shadow" />
          {config.showMetrics && <span className="text-[11px] font-semibold">{formatMetric(config.metrics.comments)}</span>}
        </div>

        <button type="button" onClick={() => onInteraction('bookmarked')} className="flex flex-col items-center gap-1" aria-label="Save">
          <Bookmark className={clsx('h-8 w-8 drop-shadow', config.interactions.bookmarked ? 'fill-[#FFD600] text-[#FFD600]' : 'fill-white text-white')} />
          <span className="text-[11px] font-semibold">Save</span>
        </button>

        <div className="flex flex-col items-center gap-1">
          <Share2 className="h-8 w-8 text-white drop-shadow" />
          {config.showMetrics && <span className="text-[11px] font-semibold">{formatMetric(config.metrics.shares)}</span>}
        </div>

        {/* Spinning sound disc */}
        <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 to-black ring-2 ring-white/20">
          <Music className="h-4 w-4 text-white" />
        </div>
      </div>
    </div>
  )
}
